import { Types, Image, Heading, Icon, Button, RichText } from '@marriott/mi-ui-library';
import { StyledBioCard, StyledBioInfo } from './BioCard.styles';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { BioCardProps } from '../GolfBioCardsContainer.types';
import { bottomPlacement, centerPlacement, topPlacement } from '../../../modules/utils/constants/constants';
import { useGetBreakpoint } from '../../../modules/utils/helpers';

export const BioCard = (props: BioCardProps) => {
  const [isTruncated, setIsTruncated] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsMobileViewPort(useGetBreakpoint() === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const richText = document.getElementById('bio-description');

    if (divRef.current && richText) {
      const containerHeight = divRef?.current?.offsetHeight;
      const lineHeight = parseInt(getComputedStyle(richText).lineHeight);

      if (containerHeight > lineHeight * 10) {
        setShowButton(true);
        setIsTruncated(true);
      } else {
        setShowButton(false);
        setIsTruncated(false);
      }
    }
  }, []);

  const toggleTruncate = () => {
    setIsTruncated(!isTruncated);
  };

  const getTitle = (props: BioCardProps) => {
    const updatedDetails = [];
    if (props?.name) updatedDetails.push(props?.name);
    if (props?.designation) updatedDetails.push(props?.designation);
    if (props?.division) updatedDetails.push(props?.division);

    return updatedDetails.toString().split(',').join(', ');
  };

  return (
    <StyledBioCard
      className={clsx(
        'd-flex',
        !isMobileViewPort
          ? props?.textAlignmentForDescription === topPlacement
            ? 'align-items-start'
            : props?.textAlignmentForDescription === centerPlacement
            ? 'align-items-center'
            : ''
          : 'align-items-center'
      )}
    >
      {props?.dynamicMedia?.assetPath && (
        <div className="bio-card__image">
          <Image
            title={props?.dynamicMedia?.altText}
            defaultImageURL={`${props?.dynamicMedia?.assetPath}`}
            customClass="bio-card__image"
            loading="eager"
            altText={props?.dynamicMedia?.altText}
            renditions={props?.dynamicMedia?.renditions}
            dynamic={props?.dynamicMedia?.dynamic}
          />
        </div>
      )}
      <StyledBioInfo descriptionAlignment={props.textAlignmentForDescription}>
        {props?.titlePlacementForCard === topPlacement && (props?.name || props?.designation || props?.division) && (
          <Heading
            variation={Types.headingType.subtitle}
            titleText={getTitle(props)}
            fontSize={Types.size.large}
            element={Types.tags.span}
          />
        )}
        {!!props?.emailId && (
          <p className="d-flex mt-2 mb-5 t-font-xs">
            <Icon iconClass="icon-mail" ariaLabel="icon-mail" />
            <span className="ml-2">
              <Button isLink={true} href={`mailto:${props?.emailId}`} aria-label={props?.emailId}>
                {props?.emailId}
              </Button>
            </span>
          </p>
        )}

        {props?.bioDescription && (
          <div ref={divRef}>
            <RichText
              componentId="bio-description"
              customClass={clsx(isTruncated ? 'truncated-description' : 'full-description', 'mb-0 t-font-m')}
              text={props?.bioDescription}
            />
            {showButton && (
              <Button
                type={Types.ButtonTypeVariation.Button}
                className="show-more-button py-2 px-0 m-0 mt-2 t-font-s"
                callback={toggleTruncate}
              >
                <span className={clsx('t-font-s')}>
                  {isTruncated ? props?.seeMoreLabel || '' : props?.seeLessLabel || ''}
                </span>
                <span
                  className={clsx('ml-1 show-more-icon', isTruncated ? 'icon-dropdown-down' : 'icon-dropdown-up')}
                ></span>
              </Button>
            )}
          </div>
        )}

        {props?.titlePlacementForCard === bottomPlacement && (props?.name || props?.designation || props?.division) && (
          <Heading
            variation={Types.headingType.body}
            titleText={getTitle(props)}
            fontSize={Types.size.medium}
            element={Types.tags.div}
            customClass="mt-5"
          />
        )}
      </StyledBioInfo>
    </StyledBioCard>
  );
};
